import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { MapPin, Mail, Phone, Send, Loader2, Building2 } from "lucide-react";
import { useLanguage } from "@/i18n";
import Reveal, { MaskedLines } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLES = ["it", "transport", "general"];

const ContactPage = () => {
  const { t, lang } = useLanguage();
  const [params] = useSearchParams();
  const initialPole = POLES.includes(params.get("pole")) ? params.get("pole") : "general";
  const [form, setForm] = useState({ name: "", email: "", phone: "", pole: initialPole, message: "" });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const err = {};
    if (form.name.trim().length < 2) err.name = t.contact.required;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) err.email = t.contact.invalidEmail;
    if (form.message.trim().length < 10) err.message = t.contact.tooShort;
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, lang });
      toast.success(t.contact.success);
      setForm({ name: "", email: "", phone: "", pole: initialPole, message: "" });
    } catch {
      toast.error(t.contact.error);
    } finally {
      setSending(false);
    }
  };

  const inputCls = (err) =>
    `w-full rounded-xl border bg-[#0A0D14] px-4 py-3 text-sm text-white placeholder-slate-600 outline-none transition-colors ${
      err ? "border-red-500" : "border-white/15 focus:border-cyan-400"
    }`;

  return (
    <main data-testid="contact-page" className="min-h-screen bg-[#070A10] px-4 pb-24 pt-32 sm:px-6">
      <div className="mx-auto max-w-6xl">
        <MaskedLines baseDelay={0.1} lines={[t.nav.contact]} lineClassName="font-mono text-xs uppercase tracking-[0.3em] text-cyan-400" />
        <MaskedLines
          baseDelay={0.25}
          lines={[t.contact.title]}
          className="mt-4"
          lineClassName="text-3xl font-extrabold uppercase tracking-tight text-white sm:text-5xl"
        />
        <Reveal delay={0.5}>
          <p className="mt-5 max-w-xl text-sm leading-relaxed text-slate-400 sm:text-base">{t.contact.desc}</p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-5">
          <Reveal className="lg:col-span-3">
            <form onSubmit={onSubmit} data-testid="contact-form" className="rounded-3xl border border-white/10 bg-[#0A0D14] p-6 sm:p-9" noValidate>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">{t.contact.name} *</label>
                  <input data-testid="contact-name-input" value={form.name} onChange={set("name")} className={inputCls(errors.name)} />
                  {errors.name && <p data-testid="contact-name-error" className="mt-1.5 text-xs text-red-400">{errors.name}</p>}
                </div>
                <div>
                  <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">{t.contact.email} *</label>
                  <input data-testid="contact-email-input" type="email" value={form.email} onChange={set("email")} className={inputCls(errors.email)} />
                  {errors.email && <p data-testid="contact-email-error" className="mt-1.5 text-xs text-red-400">{errors.email}</p>}
                </div>
                <div>
                  <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">{t.contact.phone}</label>
                  <input data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} className={inputCls(false)} />
                </div>
                <div>
                  <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">{t.contact.pole}</label>
                  <select data-testid="contact-pole-select" value={form.pole} onChange={set("pole")} className={inputCls(false)}>
                    {POLES.map((p, i) => (
                      <option key={p} value={p}>{t.contact.poleOptions[i]}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mt-5">
                <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">{t.contact.message} *</label>
                <textarea data-testid="contact-message-textarea" rows={6} value={form.message} onChange={set("message")} className={`${inputCls(errors.message)} resize-none`} />
                {errors.message && <p data-testid="contact-message-error" className="mt-1.5 text-xs text-red-400">{errors.message}</p>}
              </div>
              <button
                type="submit"
                data-testid="contact-form-submit-btn"
                disabled={sending}
                className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-cyan-400 px-8 py-4 text-sm font-bold text-slate-950 transition-colors duration-200 hover:bg-cyan-300 disabled:opacity-60 sm:w-auto"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {sending ? t.contact.sending : t.contact.submit}
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-2">
            <div className="space-y-6">
              <div className="rounded-3xl border border-white/10 bg-[#0A0D14] p-7">
                <h3 className="text-lg font-extrabold text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  {t.contact.infoTitle}
                </h3>
                <ul className="mt-5 space-y-4 text-sm text-slate-400">
                  <li className="flex items-start gap-3"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-cyan-400" /> {t.contact.addr1}</li>
                  <li className="flex items-start gap-3"><Building2 className="mt-0.5 h-4 w-4 shrink-0 text-cyan-400" /> {t.contact.ide}</li>
                  <li className="flex items-start gap-3"><Mail className="mt-0.5 h-4 w-4 shrink-0 text-cyan-400" /> {t.contact.emailLabel}</li>
                  <li className="flex items-start gap-3"><Phone className="mt-0.5 h-4 w-4 shrink-0 text-cyan-400" /> {t.contact.phoneLabel}</li>
                </ul>
              </div>
              <div className="rounded-3xl border border-cyan-400/20 bg-gradient-to-br from-[#121824] to-[#0A0D14] p-7">
                <p className="font-mono text-[10px] uppercase leading-relaxed tracking-[0.25em] text-cyan-400/90">{t.common.guarantee}</p>
                <p className="mt-4 text-sm leading-relaxed text-slate-400">{t.common.tagline}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </main>
  );
};

export default ContactPage;
